import { useState } from "react";
import { versionColumns, diffCards } from "@/mocks/lustCaution";

export default function Compare() {
  const [mode, setMode] = useState<"both" | "uncut" | "cut">("both");

  const visible = versionColumns.filter((col) => mode === "both" || col.key === mode);

  return (
    <section id="compare" className="w-full bg-background-100 scroll-mt-20">
      <div className="px-4 md:px-8 py-16 md:py-24">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl">
            <p className="text-primary-600 font-label text-sm tracking-widest uppercase">Compare</p>
            <h2 className="mt-3 font-heading text-3xl md:text-4xl font-semibold text-foreground-950 leading-snug">
              未删减版与公映版，核心差异
            </h2>
            <p className="mt-5 text-foreground-600 leading-relaxed">
              两个版本讲的是同一个故事，结局也一致。差异集中在亲密戏的呈现、片长节奏，以及个别画面的分级处理上。
              下面这种对照，可以帮你快速看清它们各自保留与舍弃了什么。
            </p>
          </div>

          <div className="mt-8 inline-flex p-1 rounded-full bg-background-200/70">
            {[
              { key: "both", label: "并排对照" },
              { key: "uncut", label: "只看未删减版" },
              { key: "cut", label: "只看公映版" },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setMode(tab.key as typeof mode)}
                className={`px-4 py-2 rounded-full text-sm font-label transition-colors cursor-pointer whitespace-nowrap ${
                  mode === tab.key
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-700 hover:text-primary-600"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className={`mt-6 grid gap-4 md:gap-6 ${visible.length === 1 ? "grid-cols-1" : "md:grid-cols-2"}`}>
            {visible.map((col) => {
              const isUncut = col.key === "uncut";
              return (
                <div
                  key={col.key}
                  className={`rounded-lg border overflow-hidden ${
                    isUncut ? "border-primary-200 bg-background-50" : "border-secondary-200 bg-background-50"
                  }`}
                >
                  <div
                    className={`px-5 py-4 flex items-center justify-between ${
                      isUncut ? "bg-primary-500 text-background-50" : "bg-secondary-700 text-background-50"
                    }`}
                  >
                    <div>
                      <h3 className="font-heading text-xl font-semibold">{col.title}</h3>
                      <p className="text-xs mt-0.5 opacity-80 font-label">{col.subtitle}</p>
                    </div>
                    <i className={isUncut ? "ri-time-line text-2xl" : "ri-scissors-cut-line text-2xl"} aria-hidden="true"></i>
                  </div>
                  <ul className="p-5 space-y-3">
                    {col.rows.map((row) => (
                      <li key={row} className="flex items-start gap-3 text-sm md:text-base text-foreground-800 leading-relaxed">
                        <i
                          className={`mt-1 shrink-0 ${isUncut ? "ri-checkbox-circle-line text-primary-500" : "ri-subtract-line text-secondary-600"}`}
                          aria-hidden="true"
                        ></i>
                        <span>{row}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {diffCards.map((card) => (
              <div key={card.title} className="rounded-lg bg-background-50 border border-background-200 p-5">
                <span className="w-11 h-11 flex items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className={`${card.icon} text-xl`} aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">{card.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}